import {
  readWorkspaceAgentPillNodeAttrs,
  WORKSPACE_AGENT_PILL_NODE_NAME,
  type WorkspaceAgentPillDocument,
  type WorkspaceAgentPillNodeAttrs,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';

/** What makes two pills the same thing to the agent — the entity, never the pill's own uuid. */
export type WorkspaceAgentPillReference =
  | { type: 'asset'; assetId: string | null; versionId: string | null }
  | { type: 'board'; boardId: string | null }
  | { type: 'contextSourceFieldValue'; contextSourceId: string | null; contextSourceFieldValueId: string | null };

const isSameWorkspaceAgentPillReference = (
  attrs: WorkspaceAgentPillNodeAttrs,
  reference: WorkspaceAgentPillReference,
): boolean => {
  if (attrs.type !== reference.type) {
    return false;
  }

  if (reference.type === 'board') {
    return !!reference.boardId && attrs.boardId === reference.boardId;
  }

  // The kit as well as the value: two kits can hold the same file, and each is its own reference.
  if (reference.type === 'contextSourceFieldValue') {
    return (
      !!reference.contextSourceId &&
      !!reference.contextSourceFieldValueId &&
      attrs.contextSourceId === reference.contextSourceId &&
      attrs.contextSourceFieldValueId === reference.contextSourceFieldValueId
    );
  }

  return !!reference.assetId && attrs.assetId === reference.assetId && attrs.versionId === reference.versionId;
};

/**
 * The pill id already standing for this asset or board, or `null`. Reused at insert time so two
 * mentions of one thing are two monikers pointing at one pill.
 */
export const findWorkspaceAgentPillIdByReference = (
  doc: WorkspaceAgentPillDocument,
  reference: WorkspaceAgentPillReference,
): string | null => {
  let existingId: string | null = null;

  doc.descendants((node) => {
    if (existingId !== null) {
      return false;
    }

    if (node.type.name !== WORKSPACE_AGENT_PILL_NODE_NAME) {
      return true;
    }

    const attrs = readWorkspaceAgentPillNodeAttrs(node.attrs);

    if (attrs && isSameWorkspaceAgentPillReference(attrs, reference)) {
      existingId = attrs.id;
    }

    return true;
  });

  return existingId;
};
